import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Ant Design
import { Breadcrumb } from "antd";
import { HomeOutlined } from "@ant-design/icons";

// project imports
import MainCard from "../MainCard";
import { useTranslation } from "react-i18next";
import { useResponsive } from '@/hooks/useResponsive';
import { usePageChrome } from '@/components/PageChrome';

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({ navigation, title, titleBottom, card, divider, ...others }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [main, setMain] = useState();
  const [item, setItem] = useState();
  const [refresh, setRefresh] = useState(true);
  const best = useRef(-1);   // how specific the item matched so far is (exact beats any prefix, a longer prefix a shorter)
  const { isDesktop } = useResponsive();
  const { crumbTail } = usePageChrome();

  // set active item state
  const getCollapse = (menu) => {
    if (menu.children) {
      menu.children.filter((collapse) => {
        if (collapse?.type && collapse?.type === "collapse") {
          getCollapse(collapse);
        } else if (collapse?.type && collapse?.type === "item") {
          // the page's own menu item: its url exactly, else the longest item url the path sits under (a page with
          // its own sub-routes, e.g. /control-tower/overview under /control-tower) — a more specific item wins
          const url = collapse.url;
          // `aliases` (menu streamlining, 2026-10-29): the routes of the pages the item holds as tabs count as its own
          const exact = location.pathname === url || !!collapse.aliases?.includes(location.pathname);
          const under = !!url && url !== "/" && location.pathname.startsWith(`${url}/`);
          const score = exact ? Number.MAX_SAFE_INTEGER : url?.length || 0;
          if ((exact || under) && score > best.current) {
            best.current = score;
            setMain(menu);
            setItem(collapse);
            localStorage.setItem("menu", JSON.stringify(menu));
            localStorage.setItem("item", JSON.stringify(collapse));
          } else {
            setRefresh(false);
          }
        }
        return false;
      });
    }
  };

  useEffect(() => {
    best.current = -1;
    navigation?.items?.map((menu) => {
      if (menu.type && menu.type === "group") {
        getCollapse(menu);
      }
      if (refresh === false) {
        try {
          const menuData = localStorage.getItem("menu");
          const itemData = localStorage.getItem("item");
          if (menuData && menuData !== "undefined") {
            setMain(JSON.parse(menuData));
          }
          if (itemData && itemData !== "undefined") {
            setItem(JSON.parse(itemData));
          }
        } catch (error) {
          console.error("Error parsing breadcrumb data:", error);
        }
        setRefresh(true);
      }
      return false;
    });
  }, [navigation]);

  // only used for component demo breadcrumbs
  if (location.pathname === "/breadcrumbs") {
    location.pathname = "/dashboard/analytics";
  }

  // DD-W1-63: a menu item may be reached from another part of the menu (its `origins`, keyed by the `from` the
  // caller passes in router state or the query). The trail then names that origin's group and parent item, both
  // looked up in the navigation by id; with no origin, or one the item does not list, the menu's own trail stands.
  const findById = (nodes, id) => {
    for (const n of nodes || []) {
      if (n?.id === id) return n;
      const hit = findById(n?.children, id);
      if (hit) return hit;
    }
    return null;
  };
  const from = location.state?.from || new URLSearchParams(location.search || "").get("from");
  const origin = item?.origins && from ? item.origins[from] : null;
  const originGroup = origin ? findById(navigation?.items, origin.group) : null;
  const originParent = origin ? findById(navigation?.items, origin.parent) : null;

  // Build breadcrumb items
  const breadcrumbItems = [];

  // Home item
  breadcrumbItems.push({
    title: (
      <Link to="/recipe-view" className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 no-underline">
        <HomeOutlined className="mr-2" />
        <span>{t("Home")}</span>
      </Link>
    ),
  });

  if (origin && originGroup && originParent) {
    breadcrumbItems.push(
      { title: <Link to={originGroup?.children?.[0]?.url} className="text-gray-600 dark:text-gray-400 hover:text-blue-600 no-underline">{originGroup.title}</Link> },
      { title: <Link to={originParent.url} className="text-gray-600 dark:text-gray-400 hover:text-blue-600 no-underline">{originParent.title}</Link> },
      { title: <span className="text-gray-900 dark:text-gray-100 font-medium">{origin.title}</span> },
    );
  }

  // Main/collapse item
  if (!(origin && originGroup && originParent) && main && main.type === "collapse") {
    breadcrumbItems.push({
      title: (
        <Link
          to={main?.children[0]?.url}
          className="text-gray-600 dark:text-gray-400 hover:text-blue-600 no-underline"
        >
          {main.title}
        </Link>
      ),
    });
  }

  // Current item (a page may add a last crumb after it, e.g. the open tab, with usePageCrumbTail)
  if (!(origin && originGroup && originParent) && item && item.type === "item") {
    breadcrumbItems.push({
      title: crumbTail ? (
        <Link to={item.url} className="text-gray-600 dark:text-gray-400 hover:text-blue-600 no-underline">{item.title}</Link>
      ) : (
        <span className="text-gray-900 dark:text-gray-100 font-medium">
          {item.title}
        </span>
      ),
    });
    if (crumbTail) breadcrumbItems.push({ title: <span className="text-gray-900 dark:text-gray-100 font-medium">{crumbTail}</span> });
  }

  // Return breadcrumb or empty div
  if (item && item.type === "item" && item.breadcrumbs !== false) {
    return (
      <MainCard
        border={false}
        className="mb-2 bg-transparent"
        {...others}
        content={false}
      >
        <div className="flex flex-col justify-start items-start mt-2">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      </MainCard>
    );
  }

  return <div />;
};

Breadcrumbs.propTypes = {
  navigation: PropTypes.object,
  title: PropTypes.bool,
  titleBottom: PropTypes.bool,
  card: PropTypes.bool,
  divider: PropTypes.bool,
};

export default Breadcrumbs;
